import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { User } from '@cms/models';

import { tenantUserApi } from '@/lib/api/tenant-user.api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CommonModal } from '@/components/common/CommonModal';

type ChangePasswordFormData = {
  password: string;
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

export interface TenantUserChangePasswordModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly user: User | null;
  readonly onSuccess?: () => void;
}

export function TenantUserChangePasswordModal({
  open,
  onClose,
  user,
  onSuccess,
}: TenantUserChangePasswordModalProps) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    defaultValues: {
      password: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({ password: '' });
    }
  }, [open, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: ChangePasswordFormData) => {
      if (!user?.id) return Promise.reject(new Error('No user'));
      return tenantUserApi.update(user.id, {
        password: data.password,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      onSuccess?.();
      onClose();
    },
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      await updateMutation.mutateAsync(data);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message ?? 'Failed to update password');
    }
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Change Password"
      titleId="tenant-user-change-password-modal-title"
      panelClassName="max-w-sm"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {user && (
          <div className="text-sm text-muted-foreground mb-4">
            Changing password for <span className="font-medium text-foreground">{user.email}</span>
          </div>
        )}

        <div>
          <label
            htmlFor="tenant-user-change-password"
            className="block text-sm font-medium text-foreground"
          >
            New Password (min 8 characters)
          </label>
          <input
            id="tenant-user-change-password"
            type="password"
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 8,
                message: 'Password must be at least 8 characters',
              },
            })}
            className={cn(inputClass, errors.password && 'border-destructive')}
          />
          {errors.password && (
            <p className="mt-1 text-sm text-destructive">
              {errors.password.message}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </CommonModal>
  );
}
